import React from 'react';
import { usePreviewContext } from '../providers/PreviewProvider';

export default function BoostReport() {
  const {
    boostReport,
    setBoostReport,
    preBoostScore,
    postBoostScore,
    preBoostResume,
    handleAcceptBoost,
    handleRejectBoost,
  } = usePreviewContext();

  const getDiffs = () => {
    if (!preBoostResume || !boostReport?.resume_data) return [];
    const diffs = [];
    const orig = preBoostResume;
    const boosted = boostReport.resume_data;

    // 1. Summary
    if (orig.summary !== boosted.summary && (orig.summary || boosted.summary)) {
      diffs.push({
        title: 'Professional Summary',
        orig: orig.summary || '(Empty)',
        boosted: boosted.summary || '(Empty)',
      });
    }

    // 2. Skills
    const origSkills = (orig.skills || []).join(', ');
    const boostedSkills = (boosted.skills || []).join(', ');
    if (origSkills !== boostedSkills) {
      diffs.push({
        title: 'Skills',
        orig: origSkills || '(None)',
        boosted: boostedSkills || '(None)',
      });
    }

    // 3. Experience
    (orig.experience || []).forEach((origExp) => {
      const boostedExp = (boosted.experience || []).find(e => e.company === origExp.company && e.role === origExp.role);
      if (boostedExp) {
        const origPoints = (origExp.points || []).join('\n');
        const boostedPoints = (boostedExp.points || []).join('\n');
        if (origPoints !== boostedPoints) {
          diffs.push({
            title: `Experience: ${origExp.company} (${origExp.role})`,
            orig: (origExp.points || []).map((p, i) => <div key={i} className="mb-1">• {p}</div>),
            boosted: (boostedExp.points || []).map((p, i) => <div key={i} className="mb-1">• {p}</div>),
          });
        }
      }
    });

    // 4. Projects
    (orig.projects || []).forEach((origProj) => {
      const title = origProj.title || origProj.name;
      const boostedProj = (boosted.projects || []).find(p => (p.title || p.name) === title);
      if (boostedProj) {
        if (origProj.description !== boostedProj.description) {
          diffs.push({
            title: `Project: ${title}`,
            orig: origProj.description || '(Empty)',
            boosted: boostedProj.description || '(Empty)',
          });
        }
      }
    });

    return diffs;
  };

  if (!boostReport || boostReport === 'confirm') return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
      <div className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0a0f1e 0%, #0f0a2a 100%)', border: '1px solid rgba(124,58,237,0.35)', boxShadow: '0 0 80px rgba(124,58,237,0.15), 0 30px 60px rgba(0,0,0,0.7)' }}>

        {/* ── Error state ── */}
        {boostReport._error ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h3 className="text-white font-bold text-lg mb-2">Boost Failed</h3>
            <p className="text-slate-400 text-sm">{boostReport._error}</p>
            <button onClick={() => setBoostReport(null)}
              className="mt-6 px-6 py-2.5 rounded-xl text-sm font-medium text-white"
              style={{ background: 'rgba(124,58,237,0.3)', border: '1px solid rgba(124,58,237,0.4)' }}>
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="shrink-0 px-6 pt-6 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #7C3AED, #4F46E5)', boxShadow: '0 0 16px rgba(124,58,237,0.5)' }}>
                    <span className="text-xl">✨</span>
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-base">Boost Complete!</h2>
                    <p className="text-slate-500 text-xs mt-0.5">Here's what was improved</p>
                  </div>
                </div>
              </div>
              {/* Stats row */}
              <div className="flex gap-3 mt-4">
                {[
                  { label: 'Sections Polished', value: (boostReport.polished_sections || []).length, color: '#7C3AED' },
                  { label: 'Metrics Added', value: boostReport.metrics_added || 0, color: '#10b981' },
                  { label: 'Auto-Filled', value: (boostReport.sections_auto_filled || []).length, color: '#3b82f6' },
                ].map((stat, i) => (
                  <div key={i} className="flex-1 rounded-xl px-3 py-2.5 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="text-xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>
              {/* ATS Score Comparison */}
              <div className="mt-4 p-4 rounded-xl flex items-center justify-around"
                style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)' }}>
                <div className="text-center">
                  <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Before Boost</div>
                  <div className="text-2xl font-bold text-slate-400 mt-1">{preBoostScore || 0}%</div>
                </div>
                
                <div className="flex flex-col items-center">
                  <span className="text-violet-400 text-lg font-bold">➔</span>
                  {postBoostScore !== null && postBoostScore > (preBoostScore || 0) && (
                    <span className="text-[10px] text-emerald-400 font-semibold mt-0.5">
                      +{postBoostScore - (preBoostScore || 0)}% Increase
                    </span>
                  )}
                </div>

                <div className="text-center">
                  <div className="text-xs text-violet-400 font-semibold uppercase tracking-wider">Estimated ATS</div>
                  <div className="text-3xl font-extrabold text-white mt-1 flex items-baseline justify-center gap-1" 
                    style={{ textShadow: '0 0 15px rgba(124,58,237,0.6)' }}>
                    {postBoostScore !== null ? `${postBoostScore}%` : 'Calculating...'}
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

              {/* What was improved */}
              {(boostReport.improvements || []).length > 0 && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-2">✅ What was improved</p>
                  <div className="space-y-1.5">
                    {(boostReport.improvements || []).map((imp, i) => (
                      <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl"
                        style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.15)' }}>
                        <svg className="w-3.5 h-3.5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                        <span className="text-emerald-300 text-xs leading-relaxed">{imp}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Changes Diff */}
              {getDiffs().length > 0 && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-2">🔍 Detailed Changes (Before vs After)</p>
                  <div className="space-y-3">
                    {getDiffs().map((diff, idx) => (
                      <div key={idx} className="rounded-xl overflow-hidden border border-slate-800 bg-black/30">
                        {/* Title bar */}
                        <div className="px-3 py-2 bg-slate-900/60 border-b border-slate-800 text-[11px] font-bold text-violet-300">
                          {diff.title}
                        </div>
                        {/* Stacked comparison */}
                        <div className="divide-y divide-slate-800/40">
                          {/* Original (Before) */}
                          <div className="p-3 bg-red-950/5">
                            <div className="text-[9px] uppercase font-extrabold text-red-500/80 tracking-wider mb-1">Original</div>
                            <div className="text-xs text-slate-400 line-through leading-relaxed decoration-red-900/80 decoration-1">
                              {diff.orig}
                            </div>
                          </div>
                          {/* Optimized (After) */}
                          <div className="p-3 bg-emerald-950/10">
                            <div className="text-[9px] uppercase font-extrabold text-emerald-400 tracking-wider mb-1">Optimized</div>
                            <div className="text-xs text-emerald-100/90 leading-relaxed font-medium">
                              {diff.boosted}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Auto-filled sections */}
              {(boostReport.sections_auto_filled || []).length > 0 && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-2">✨ Auto-generated sections</p>
                  <div className="flex flex-wrap gap-2">
                    {(boostReport.sections_auto_filled || []).map((sec, i) => (
                      <span key={i} className="px-3 py-1.5 rounded-full text-xs font-semibold capitalize"
                        style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd' }}>
                        {sec}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Gaps requiring input */}
              {(boostReport.gaps_requiring_input || []).length > 0 && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-2">⚠️ Still needs your input</p>
                  <div className="space-y-1.5">
                    {(boostReport.gaps_requiring_input || []).map((gap, i) => (
                      <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl"
                        style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.15)' }}>
                        <span className="text-amber-400 text-xs shrink-0 mt-0.5">→</span>
                        <span className="text-amber-300 text-xs leading-relaxed">{gap}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="shrink-0 px-6 py-4 flex gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button onClick={handleRejectBoost}
                className="flex-1 py-3 rounded-xl text-sm font-medium text-slate-400 transition-all hover:text-white hover:bg-white/5"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                Discard Changes
              </button>
              <button onClick={() => handleAcceptBoost(boostReport.resume_data)}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #7C3AED, #4F46E5)', boxShadow: '0 0 20px rgba(124,58,237,0.3)' }}>
                Accept & Save ✨
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
